import moment from "moment";
import type { RangePreset } from "api/analytics-dashboards";

export interface ModuleDateRangePreset {
  value: string;
  label: string;
  isNone?: boolean;
  isCustom?: boolean;
  getDateRange?: () => { startDate: string; endDate: string };
}

export interface ModuleDateRangeValue {
  value: string;
  startDate?: string;
  endDate?: string;
}

const fmt = (d: moment.Moment) => d.format("YYYY-MM-DD");
const today = () => moment().startOf("day");
const daysAgo = (n: number) => today().subtract(n, "days");

const MODULE_DATE_RANGE_VALUES: Record<string, RangePreset> = {
  LAST_7: "last_7_days",
  LAST_30: "last_30_days",
  LAST_60: "last_60_days",
  LAST_90: "last_90_days",
  LAST_12_MONTHS: "last_12_months",
  ALL_TIME: "all_time",
  CUSTOM: "custom"
};

export const MODULE_DATE_PRESETS: ModuleDateRangePreset[] = [
  {
    value: MODULE_DATE_RANGE_VALUES.LAST_7,
    label: "Last 7 days",
    getDateRange: () => ({ startDate: fmt(daysAgo(6)), endDate: fmt(today()) })
  },
  {
    value: MODULE_DATE_RANGE_VALUES.LAST_30,
    label: "Last 30 days",
    getDateRange: () => ({ startDate: fmt(daysAgo(29)), endDate: fmt(today()) })
  },
  {
    value: MODULE_DATE_RANGE_VALUES.LAST_60,
    label: "Last 60 days",
    getDateRange: () => ({ startDate: fmt(daysAgo(59)), endDate: fmt(today()) })
  },
  {
    value: MODULE_DATE_RANGE_VALUES.LAST_90,
    label: "Last 90 days",
    getDateRange: () => ({ startDate: fmt(daysAgo(89)), endDate: fmt(today()) })
  },
  {
    value: MODULE_DATE_RANGE_VALUES.LAST_12_MONTHS,
    label: "Last 12 months",
    getDateRange: () => ({
      startDate: fmt(today().subtract(12, "months")),
      endDate: fmt(today())
    })
  },
  {
    value: MODULE_DATE_RANGE_VALUES.ALL_TIME,
    label: "All time",
    isNone: true
  },
  {
    value: MODULE_DATE_RANGE_VALUES.CUSTOM,
    label: "Custom",
    isCustom: true
  }
];

export const DEFAULT_MODULE_DATE_RANGE: ModuleDateRangeValue = {
  value: MODULE_DATE_RANGE_VALUES.ALL_TIME
};

// The selected range, worded exactly as the filter's own trigger words it, so
// the graph subtitles and the filter button can never disagree.
export const moduleDateRangeLabel = (
  value: ModuleDateRangeValue,
  presets: ModuleDateRangePreset[] = MODULE_DATE_PRESETS
): string => {
  if (value.value === "custom" && value.startDate && value.endDate)
    return value.startDate === value.endDate
      ? value.startDate
      : `${value.startDate} - ${value.endDate}`;
  return presets.find((p) => p.value === value.value)?.label ?? "";
};

export const resolvePresetRange = (
  value: string,
  presets: ModuleDateRangePreset[] = MODULE_DATE_PRESETS
): { startDate?: string; endDate?: string } => {
  const preset = presets.find((p) => p.value === value);
  if (!preset?.getDateRange) return {};
  const { startDate, endDate } = preset.getDateRange();
  return { startDate, endDate };
};

export const moduleRangeToDateFilter = (
  next: ModuleDateRangeValue,
  partnerCreatedAt: string
): { start_date: Date; end_date: Date } => {
  if (next.startDate && next.endDate)
    return {
      start_date: moment(next.startDate).startOf("day").toDate(),
      end_date: moment(next.endDate).endOf("day").toDate()
    };
  return {
    start_date: moment(partnerCreatedAt).startOf("day").toDate(),
    end_date: moment().endOf("day").toDate()
  };
};

export const moduleRangeToDatePickerMode = (
  next: ModuleDateRangeValue
): "all_time" | "custom" | 7 | 30 | 60 | 90 | 365 => {
  switch (next.value) {
    case "last_7_days":
      return 7;
    case "last_30_days":
      return 30;
    case "last_60_days":
      return 60;
    case "last_90_days":
      return 90;
    case "last_12_months":
      return 365;
    case "all_time":
      return "all_time";
    default:
      return "custom";
  }
};
